/**
 * CutsceneManager - Manages cutscene sequences and dialogue
 * Visual novel style with background scene images and dialogue box overlay
 */

import React, { useState, useEffect } from 'react';
import { DialogueBox } from './DialogueBox';

export interface DialogueLine {
  text: string;
  speaker?: string;
  portrait?: string;
  autoAdvance?: boolean;
  delay?: number; // Delay before showing this line (in ms)
  sceneImage?: string; // Optional per-dialogue scene image override
}

export interface Cutscene {
  id: string;
  dialogues: DialogueLine[];
  onComplete?: () => void;
  skippable?: boolean;
  backgroundImage?: string; // Default background for the cutscene
}

interface CutsceneManagerProps {
  cutscene: Cutscene | null;
  onComplete?: () => void;
  onSkip?: () => void;
}

export const CutsceneManager: React.FC<CutsceneManagerProps> = ({
  cutscene,
  onComplete,
  onSkip
}) => {
  const [currentDialogueIndex, setCurrentDialogueIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [delayTimer, setDelayTimer] = useState<number | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (cutscene) {
      setIsPlaying(true);
      setCurrentDialogueIndex(0);
      setDelayTimer(null);
      setImageLoaded(false);
    } else {
      setIsPlaying(false);
    }
  }, [cutscene]);

  useEffect(() => {
    // Handle delay before showing dialogue
    if (cutscene && isPlaying && currentDialogueIndex < cutscene.dialogues.length) {
      const currentDialogue = cutscene.dialogues[currentDialogueIndex];
      if (currentDialogue && currentDialogue.delay && currentDialogue.delay > 0) {
        const timeout = setTimeout(() => {
          setDelayTimer(null);
        }, currentDialogue.delay);
        setDelayTimer(timeout as any);
        return () => clearTimeout(timeout);
      }
    }
    return undefined;
  }, [currentDialogueIndex, cutscene, isPlaying]);

  const handleDialogueComplete = () => {
    // Dialogue text finished typing
  };

  const handleDialogueSkip = () => {
    if (!cutscene) return;

    // Move to next dialogue
    if (currentDialogueIndex < cutscene.dialogues.length - 1) {
      setCurrentDialogueIndex(currentDialogueIndex + 1);
    } else {
      // Cutscene complete
      completeCutscene();
    }
  };

  const completeCutscene = () => {
    setIsPlaying(false);
    cutscene?.onComplete?.();
    onComplete?.();
  };

  const skipCutscene = () => {
    if (cutscene?.skippable) {
      setIsPlaying(false);
      onSkip?.();
    }
  };

  if (!cutscene || !isPlaying) {
    return null;
  }

  const currentDialogue = cutscene.dialogues[currentDialogueIndex];

  // Get the current scene image (dialogue-specific or default background)
  const currentSceneImage = currentDialogue?.sceneImage || cutscene.backgroundImage;

  // During delay period, show only the scene
  if (delayTimer !== null) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: '#000000',
        zIndex: 4999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {/* Scene Image */}
        {currentSceneImage && (
          <img
            src={currentSceneImage}
            alt="Scene"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
        )}
        {/* Subtle vignette overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.4) 100%)',
          pointerEvents: 'none',
        }} />
      </div>
    );
  }

  // Check if currentDialogue exists
  if (!currentDialogue) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: '#000000',
      zIndex: 4999,
    }}>
      {/* Scene Image - Full screen background */}
      {currentSceneImage && (
        <img
          src={currentSceneImage}
          alt="Scene"
          onLoad={() => setImageLoaded(true)}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      )}

      {/* Gradient overlay at bottom for dialogue box readability */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '40%',
        background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.9) 100%)',
        pointerEvents: 'none',
      }} />

      {/* Subtle vignette overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.3) 100%)',
        pointerEvents: 'none',
      }} />

      {/* Dialogue Box */}
      <DialogueBox
        text={currentDialogue.text}
        speaker={currentDialogue.speaker}
        portrait={currentDialogue.portrait}
        autoAdvance={currentDialogue.autoAdvance}
        onComplete={handleDialogueComplete}
        onSkip={handleDialogueSkip}
        visible={true}
      />

      {/* Skip cutscene button */}
      {cutscene.skippable && (
        <button
          onClick={skipCutscene}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '10px 20px',
            fontSize: '12px',
            fontWeight: 'bold',
            fontFamily: 'monospace',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: '#888',
            border: '2px solid #444',
            borderRadius: '4px',
            cursor: 'pointer',
            zIndex: 5001,
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#ffffff';
            e.currentTarget.style.borderColor = '#ffffff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#888';
            e.currentTarget.style.borderColor = '#444';
          }}
        >
          SKIP CUTSCENE (ESC)
        </button>
      )}

      {/* Progress indicator */}
      <div style={{
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '12px',
        color: 'rgba(255,255,255,0.6)',
        fontFamily: 'monospace',
        zIndex: 5001,
        textShadow: '0 0 4px rgba(0,0,0,0.8)',
      }}>
        {currentDialogueIndex + 1} / {cutscene.dialogues.length}
      </div>
    </div>
  );
};
